import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'uptime-status-language';
const DEFAULT_LANGUAGE = 'es';

const localeMap = {
    es: 'es-ES',
    en: 'en-GB',
    eu: 'eu-ES'
};

const languageOptions = [
    { code: 'es', label: 'ES', nativeName: 'Castellano' },
    { code: 'en', label: 'EN', nativeName: 'English' },
    { code: 'eu', label: 'EU', nativeName: 'Euskara' }
];

const translations = {
    es: {
        app: {
            loading: 'Cargando...'
        },
        common: {
            appName: 'Uptime Status',
            loading: 'Cargando...',
            loadingDetails: 'Cargando detalle...',
            cancel: 'Cancelar',
            save: 'Guardar',
            saving: 'Guardando...',
            delete: 'Eliminar',
            menu: 'Menú',
            accept: 'Aceptar',
            back: 'Volver',
            close: 'Cerrar',
            expand: 'Expandir',
            collapse: 'Contraer',
            logout: 'Salir',
            edit: 'Editar',
            dismiss: 'Descartar',
            dismissing: 'Descartando...',
            create: 'Crear',
            new: 'Nuevo',
            total: 'Total',
            error: 'Error',
            userFallback: 'Usuario',
            noData: 'Sin datos',
            status: {
                operational: 'Operativo',
                down: 'Caído',
                regionalIssue: 'Bloqueo LaLiga',
                unknown: 'Desconocido',
                paused: 'Pausado',
                open: 'Abierta',
                resolved: 'Resuelta',
                dismissed: 'Descartada',
                acknowledged: 'Reconocida',
                public: 'Pública',
                private: 'Privada'
            },
            language: {
                label: 'Idioma'
            },
            pagination: {
                previous: 'Anterior',
                next: 'Siguiente',
                page: 'Página {page} de {total}',
                results: '{start}-{end} de {total}'
            },
            durations: {
                lessThanMinute: 'Menos de 1 min',
                daysShort: 'd',
                hoursShort: 'h',
                minutesShort: 'min'
            }
        },
        layout: {
            nav: {
                dashboard: 'Panel',
                incidents: 'Incidencias',
                statusPages: 'Páginas de estado'
            }
        },
        login: {
            title: 'Iniciar sesión',
            subtitle: 'Accede a tus monitores y páginas de estado.',
            emailLabel: 'Correo electrónico',
            passwordLabel: 'Contraseña',
            emailPlaceholder: 'admin@localhost',
            passwordPlaceholder: 'Tu contraseña',
            submit: 'Entrar',
            submitting: 'Accediendo...',
            error: 'No se pudo iniciar sesión',
            noAccount: 'No tienes cuenta?',
            createAccount: 'Crear cuenta'
        },
        register: {
            disabledTitle: 'Registro deshabilitado',
            disabledDescription: 'No se permiten nuevas altas de usuario en este entorno.',
            backToLogin: 'Volver al acceso',
            title: 'Crear cuenta',
            subtitle: 'Configura tu acceso para gestionar monitores.',
            fullNameLabel: 'Nombre completo',
            fullNamePlaceholder: 'Tu nombre',
            emailLabel: 'Correo electrónico',
            emailPlaceholder: 'tu@email.com',
            passwordLabel: 'Contraseña',
            passwordPlaceholder: 'Mínimo 6 caracteres',
            submit: 'Crear cuenta',
            submitting: 'Creando cuenta...',
            error: 'No se pudo crear la cuenta',
            hasAccount: 'Ya tienes cuenta?',
            signIn: 'Inicia sesión'
        },
        incidents: {
            title: 'Incidencias',
            subtitle: 'Todas las incidencias de cualquiera de tus monitores, ordenadas de más reciente a más antigua.',
            cards: {
                total: 'Total',
                open: 'Abiertas',
                resolved: 'Resueltas'
            },
            historyTitle: 'Historial global',
            loading: 'Cargando incidencias...',
            empty: 'No hay incidencias registradas en ninguno de tus monitores.',
            loadError: 'No se pudieron cargar las incidencias',
            monitorLabel: 'Monitor',
            startLabel: 'Inicio',
            endLabel: 'Fin',
            durationLabel: 'Duración',
            noDetails: 'Sin detalle adicional',
            viewMonitor: 'Ver monitor',
            dismissError: 'No se pudo descartar la incidencia',
            hiddenFromPublic: 'Oculta en la vista pública'
        },
        statusPageIncidents: {
            loading: 'Cargando incidencias...',
            loadError: 'No se pudieron cargar las incidencias',
            backToStatusPages: 'Volver a páginas de estado',
            title: 'Incidencias: {name}',
            subtitle: 'Listado completo de incidencias de la página, sin agrupar por servicio y ordenado de más reciente a más antigua.',
            openPublicPage: 'Abrir página pública',
            backToConfig: 'Volver a configuración',
            cards: {
                total: 'Total',
                open: 'Abiertas',
                resolved: 'Resueltas'
            },
            historyTitle: 'Historial de incidencias',
            startLabel: 'Inicio',
            endLabel: 'Fin',
            durationLabel: 'Duración',
            noDetails: 'Sin detalle adicional',
            empty: 'No hay incidencias registradas para esta página.'
        },
        publicStatusPage: {
            notFound: 'Página de estado no encontrada o no es pública',
            serviceStatus: 'Estado del servicio',
            pageStatus: 'Estado de la página',
            lastUpdated: 'Última actualización {time}',
            pageRefreshedAt: 'Página actualizada {time}',
            autoRefresh: 'Actualización automática cada {seconds} s',
            liveUpdates: 'Actualización en tiempo real',
            summary: {
                hasDown: 'Algunos servicios con incidencias',
                hasRegionalIssues: 'Algunos servicios tienen bloqueos de LaLiga',
                allUp: 'Todos los sistemas operativos',
                partial: 'Estado parcial del servicio'
            },
            services: 'Servicios',
            monitorsCount: '{count} monitores',
            ungrouped: 'Sin grupo',
            uptime: 'Uptime',
            viewDetails: 'Ver detalle',
            laligaWarningHelp: 'Acceso afectado de forma regional: puede depender del ISP del visitante.',
            poweredBy: 'Funciona con Uptime Status'
        },
        publicMonitorDetail: {
            notFound: 'Servicio no encontrado en esta página de estado',
            backToStatus: 'Volver a la lista',
            checkedEvery: 'Comprobado cada {minutes} minutos',
            uptimeTitle: 'Uptime',
            overallUptime: 'Uptime general',
            responseTimeTitle: 'Tiempo de respuesta',
            recentEvents: 'Eventos recientes',
            avgResponseTime: 'Tiempo medio de respuesta',
            maxResponseTime: 'Tiempo máximo de respuesta',
            minResponseTime: 'Tiempo mínimo de respuesta',
            last24Hours: 'Últimas 24 horas',
            last7Days: 'Últimos 7 días',
            last30Days: 'Últimos 30 días',
            last90Days: 'Últimos 90 días',
            last2Days: 'Últimos 2 días',
            noResponseData: 'No hay datos de tiempo de respuesta para este periodo',
            noEvents: 'No hay eventos recientes para este servicio',
            reasonWas: 'La razón fue {reason}.',
            events: {
                runningAgain: 'Operativo de nuevo',
                currentIssue: 'Incidencia activa',
                downtime: 'Caído durante {duration}',
                openSince: 'Abierto desde {time}'
            }
        },
        modal: {
            close: 'Cerrar'
        },
        dashboard: {
            status: {
                paused: 'Pausado',
                operational: 'Operativo',
                down: 'Caído',
                laligaBlocked: 'Bloqueo LaLiga',
                unknown: 'Desconocido'
            },
            laligaWarningHelp: 'Advertencia regional: algunos usuarios pueden acceder y otros no, según su ISP.',
            errors: {
                saveMonitor: 'No se pudo guardar el monitor',
                deleteMonitor: 'Error eliminando monitor: {error}',
                reorderPanel: 'No se pudo reordenar el panel',
                updateStatus: 'No se pudo actualizar el estado del monitor',
                duplicateNameMustDiffer: 'Al duplicar, el nombre debe ser distinto del monitor original.',
                duplicateUrlMustDiffer: 'Al duplicar, la URL debe ser distinta del monitor original.',
                bulkUpdate: 'No se pudieron aplicar los cambios en lote.',
                bulkDelete: 'No se pudieron eliminar los monitores seleccionados.',
                bulkFieldsRequired: 'Selecciona al menos un campo para actualizar.',
                bulkIntervalInvalid: 'El intervalo debe ser un numero igual o superior a 60 segundos.',
                bulkTimeoutInvalid: 'El timeout debe ser un numero igual o superior a 1 segundo.',
                bulkExpectedCodeInvalid: 'El codigo esperado debe estar entre 100 y 599.',
                bulkTcpPortInvalid: 'El puerto TCP debe estar entre 1 y 65535.'
            },
            title: 'Panel',
            subtitle: 'Monitoreo en tiempo real de tus servicios',
            newMonitor: 'Añadir monitor',
            cards: {
                total: 'Total',
                operational: 'Operativos',
                down: 'Caídos',
                blocked: 'Bloqueos LaLiga',
                paused: 'Pausados'
            },
            modal: {
                editTitle: 'Editar monitor',
                newTitle: 'Nuevo monitor',
                duplicateTitle: 'Duplicar monitor'
            },
            sections: {
                whatTitle: 'Qué monitorizar',
                whatDescription: 'Define el servicio y el punto exacto al que debe llegar la comprobación.',
                frequencyTitle: 'Frecuencia de comprobación',
                frequencyDescription: 'Ajusta cada cuánto se revisa el servicio y cuánto tiempo se espera antes de marcar fallo.',
                validationTitle: 'Criterios de validación',
                validationDescription: 'Refina cómo se decide si la comprobación es correcta según el tipo de monitor.',
                listTitle: 'Monitores configurados',
                listDescription: 'Puedes mover cada monitor para cambiar su posición en el panel.'
            },
            fields: {
                name: 'Nombre',
                group: 'Grupo',
                type: 'Tipo',
                intervalSeconds: 'Intervalo (seg)',
                timeoutSeconds: 'Timeout (seg)',
                httpMethod: 'Método HTTP',
                expectedCode: 'Código esperado',
                expectedKeyword: 'Palabra clave esperada',
                tcpPort: 'Puerto TCP',
                host: 'Host',
                hostOrIp: 'Host o IP',
                url: 'URL'
            },
            placeholders: {
                apiName: 'Ej. Mi API Principal',
                group: 'Ej. Core, Frontend, Infraestructura',
                host: 'db.ejemplo.com',
                hostOrIp: '8.8.8.8',
                urlHttp: 'http://api.ejemplo.com/health',
                urlHttps: 'https://api.ejemplo.com/health',
                expectedCode: '200',
                expectedKeyword: 'healthy',
                tcpPort: '443',
                bulkClearable: 'Dejalo vacio para limpiar este valor'
            },
            helps: {
                name: 'Es el nombre que verás en el panel y en las incidencias.',
                group: 'Usa un nombre compartido para ordenar monitores relacionados en el panel y la página pública.',
                type: 'Cambia solo el tipo de comprobación; el resto del formulario se adapta automáticamente.',
                host: 'Indica solo el host. El puerto se configura en el campo específico de TCP.',
                hostOrIp: 'Usa un host o una dirección IP que deba responder al ping.',
                url: 'Usa la URL exacta que debe responder a la comprobación.',
                intervalSeconds: 'Mínimo 60 segundos. Usa valores más altos para servicios poco críticos.',
                timeoutSeconds: 'Tiempo máximo de espera antes de considerar que la comprobación no responde.',
                httpMethod: 'GET suele ser la opción adecuada para endpoints de estado o salud.',
                expectedCode: 'Opcional. Si lo dejas vacío, cualquier código HTTP se evaluará junto con el resto de la comprobación.',
                expectedKeyword: 'Opcional. Solo marcará OK si la respuesta contiene este texto.',
                tcpPort: 'Indica el puerto que debe aceptar conexiones desde el worker.',
                ping: 'Para un monitor de tipo Ping solo necesitas el host o IP. No hay validaciones adicionales.'
            },
            summary: {
                noFixedCode: 'sin código fijo',
                contains: 'contiene "{keyword}"',
                tcpConnection: 'Conexión TCP al puerto {port}',
                tcpNoPort: 'Conexión TCP sin puerto definido',
                ping: 'Comprobación de conectividad por ping'
            },
            bulkEdit: {
                title: 'Editar {count} monitores',
                organizationTitle: 'Organización',
                organizationDescription: 'Asigna un grupo común o limpia el grupo de los monitores seleccionados.',
                frequencyTitle: 'Frecuencia y timeout',
                frequencyDescription: 'Activa solo los campos que quieras aplicar a todos los monitores seleccionados.',
                validationTitle: 'Criterios de validacion',
                validationDescription: 'Si activas un campo y lo dejas vacio, se limpiara en todos los monitores seleccionados.',
                blankClearsField: 'Si activas este campo y lo dejas vacio, se eliminara de todos los monitores seleccionados.',
                mixedSelectionHelp: 'La seleccion mezcla tipos de monitor distintos. En lote solo se muestran las validaciones compatibles para todos.',
                noValidationFields: 'Esta seleccion no tiene criterios de validacion comunes editables en lote.'
            },
            filters: {
                toggle: 'Filtros',
                search: 'Buscar',
                searchPlaceholder: 'Nombre, URL, grupo o CDN',
                status: 'Estado',
                allStatuses: 'Todos los estados',
                active: 'Activos',
                notifications: 'Notificaciones',
                allNotifications: 'Todas',
                withNotifications: 'Con alertas',
                withoutNotifications: 'Sin alertas',
                emailNotifications: 'Solo email',
                telegramNotifications: 'Solo Telegram',
                webhookNotifications: 'Solo webhook',
                allTypes: 'Todos los tipos',
                allGroups: 'Todos los grupos',
                cdn: 'CDN',
                allCdns: 'Todos los CDN',
                withoutCdn: 'Sin CDN',
                results: '{shown} de {total}',
                clear: 'Limpiar',
                noResults: 'No hay monitores que coincidan con los filtros.'
            },
            actions: {
                saveChanges: 'Guardar cambios',
                createMonitor: 'Crear monitor',
                duplicateMonitor: 'Duplicar monitor',
                alertsSelected: 'Alertas',
                editSelected: 'Editar',
                pauseSelected: 'Pausar',
                resumeSelected: 'Reanudar',
                deleteSelected: 'Eliminar',
                applyBulkEdit: 'Aplicar cambios',
                selectAll: 'Seleccionar todos',
                clearSelection: 'Limpiar seleccion',
                selectedCount: '{count} seleccionados',
                selectMonitor: 'Seleccionar monitor {name}',
                moveUp: 'Mover arriba',
                moveDown: 'Mover abajo',
                pauseMonitor: 'Pausar monitor',
                resumeMonitor: 'Reanudar monitor',
                checkEvery: 'Cada {minutes} min',
                checkStopped: 'Comprobación detenida',
                unchecked: 'Sin comprobar'
            },
            loading: 'Cargando...',
            empty: 'No hay monitores configurados. Crea uno para comenzar.',
            deleteTitle: 'Eliminar monitor',
            deletePrompt: 'Eliminar el monitor "{name}" permanentemente?',
            bulkDeleteTitle: 'Eliminar {count} monitores',
            bulkDeletePrompt: '¿Eliminar permanentemente los {count} monitores seleccionados? Esta acción no se puede deshacer.',
            bulkAlertsTitle: 'Alertas para {count} monitores',
            bulkAlertsDescription: 'Esta configuración reemplazará las alertas de todos los monitores seleccionados.',
            bulkAlertsSave: 'No se pudieron guardar las alertas en lote.',
            bulkAlertsPrefillConfigured: 'Se han precargado los valores compartidos de {configured} monitores con preferencias guardadas.',
            bulkAlertsPrefillDefaults: 'Ninguno de los {total} monitores seleccionados tiene preferencias de alertas guardadas todavía. El modal parte de la configuración por defecto.',
            bulkAlertsPrefillConflicts: 'Se han detectado varias configuraciones guardadas distintas. Elige abajo cuál quieres cargar como base antes de aplicarla a todos los monitores seleccionados.',
            bulkAlertsSourceLabel: 'Configuración base',
            bulkAlertsSourceHelp: 'Puedes cargar una de las configuraciones guardadas detectadas y ajustarla después si hace falta antes de aplicarla a toda la selección.',
            bulkAlertsSourceShared: 'Mantener solo los valores compartidos',
            bulkAlertsSourceCustom: 'Valores ajustados manualmente',
            bulkAlertsSourceMoreMonitors: '{name} y {count} más',
            groups: {
                ungrouped: 'Sin grupo',
                manage: 'Grupos',
                manageTitle: 'Gestionar grupos',
                manageDescription: 'Añade, renombra o elimina grupos. Al eliminar un grupo, sus monitores quedan sin grupo.',
                empty: 'Aún no hay grupos creados.',
                createTitle: 'Asignar monitor a grupo',
                create: 'Asignar grupo',
                createHelp: 'El monitor seleccionado se mueve al grupo indicado. Si el grupo no existe, se crea al asignarlo.',
                assignTo: 'Monitor',
                noMonitors: 'No hay monitores disponibles',
                newName: 'Nombre del grupo',
                insertTyped: 'Usar "{name}"',
                rename: 'Renombrar',
                clear: 'Eliminar grupo',
                nameRequired: 'El nombre del grupo no puede estar vacío.',
                monitorRequired: 'Selecciona el monitor que quieres asignar o mover.',
                updateError: 'No se pudo actualizar el grupo.'
            },
            technical: {
                cdn: 'CDN',
                sslDays: '{days} d',
                sslExpired: 'Caducado',
                sslError: 'Error'
            }
        },
        statusPages: {
            errors: {
                savePage: 'No se pudo guardar la status page',
                deletePage: 'Error eliminando: {error}',
                loadDetail: 'Error cargando detalle:',
                loadPage: 'Error cargando status page: {error}'
            },
            title: 'Páginas de estado',
            subtitle: 'Configura páginas públicas de estado',
            newPage: 'Nueva página de estado',
            modal: {
                editTitle: 'Editar página de estado',
                newTitle: 'Nueva página de estado'
            },
            sections: {
                identityTitle: 'Identidad pública',
                identityDescription: 'Configura el nombre y la URL con la que los usuarios verán esta página.',
                publicationTitle: 'Publicación y personalización',
                publicationDescription: 'Ajusta si la página es visible y los datos visuales básicos que quieres mostrar.',
                monitorsTitle: 'Monitores visibles',
                monitorsDescription: 'Selecciona qué monitores formarán parte de esta página y en qué orden aparecerán.',
                orderTitle: 'Orden en la página pública',
                orderDescription: 'El primer monitor de la lista aparecerá arriba del todo en la página pública.'
            },
            fields: {
                slug: 'Slug (URL)',
                name: 'Nombre',
                description: 'Descripción',
                logoUrl: 'URL del Logo',
                monitorSearch: 'Buscar monitores',
                theme: 'Tema',
                public: 'Pública'
            },
            placeholders: {
                slug: 'mi-servicio',
                name: 'Estado de Mi Servicio',
                description: 'Página de estado pública...',
                logoUrl: 'https://...',
                monitorSearch: 'Buscar por nombre, URL o grupo',
                previewSlug: 'mi-servicio'
            },
            helps: {
                slug: 'Se usa en la URL pública. Mejor corto, estable y sin espacios.',
                name: 'Opcional. Si lo dejas vacío, en la página pública se mostrará solo el logo y el resto del contenido.',
                description: 'Opcional. Sirve para explicar qué servicios cubre esta página o a quién va dirigida.',
                logoUrl: 'Usa una imagen ligera y pública, por ejemplo el logotipo del servicio.',
                theme: 'Solo afecta a la apariencia de la página pública.',
                public: 'Si la desactivas, seguirá existiendo pero no se mostrará como página pública.',
                noMonitors: 'Puedes guardar la página sin monitores, pero no mostrará estado operativo hasta que selecciones al menos uno.',
                noSearchResults: 'Ningún monitor coincide con la búsqueda actual.',
                selectedCount: '{count} seleccionados',
                orderItem: '{index}. {label}',
                urlPreview: 'Vista previa de la URL'
            },
            themes: {
                light: 'Claro',
                dark: 'Oscuro'
            },
            actions: {
                selectAll: 'Seleccionar todos',
                clear: 'Limpiar',
                saveChanges: 'Guardar cambios',
                createPage: 'Crear página de estado',
                moveUp: 'Mover arriba',
                moveDown: 'Mover abajo',
                viewPublic: 'Ver pública',
                viewIncidents: 'Ver incidencias',
                openPublicPage: 'Abrir página pública'
            },
            listTitle: 'Mis páginas de estado',
            loading: 'Cargando...',
            empty: 'No hay páginas de estado creadas',
            detailTitle: 'Detalle: {name}',
            detailSubtitle: 'Resumen operativo y configuración visible de la página pública.',
            cards: {
                visibility: 'Visibilidad',
                effectiveUrl: 'URL efectiva',
                monitors: 'Monitores',
                description: 'Descripción',
                unavailable: 'No disponible',
                internalRoute: 'Ruta pública interna',
                assigned: '{count} asignados',
                statusSummary: '{up} operativos, {down} caídos',
                noDescription: 'Sin descripción configurada',
                originName: 'Nombre origen',
                lastCheck: 'Última comprobación',
                noLastCheck: 'Sin datos',
                noMonitors: 'No hay monitores asignados.'
            },
            deleteTitle: 'Eliminar página de estado',
            deletePrompt: 'Eliminar la página de estado "{name}"?',
            publicRoute: 'Ruta pública interna'
        },
        monitorDetail: {
            loading: 'Cargando detalle...',
            notFound: 'Monitor no encontrado',
            status: {
                paused: 'Pausado',
                operational: 'Operativo',
                down: 'Caído',
                laligaBlocked: 'Bloqueo LaLiga',
                unknown: 'Desconocido'
            },
            buttons: {
                alerts: 'Alertas',
                resetHistory: 'Resetear historial',
                resetting: 'Reseteando...',
                pauseMonitor: 'Pausar monitor',
                resumeMonitor: 'Reanudar monitor',
                updating: 'Actualizando...'
            },
            errors: {
                updateStatus: 'No se pudo actualizar el estado del monitor',
                resetHistory: 'No se pudo resetear el historial del monitor',
                dismissIncident: 'No se pudo descartar la incidencia',
                saveUptimeOverride: 'No se pudo guardar el ajuste manual de uptime',
                resetUptimeOverride: 'No se pudo restablecer el ajuste manual de uptime',
                resetUptimeOverrides: 'No se pudieron restablecer los ajustes manuales de uptime',
                loadAlerts: 'No se pudo cargar la configuración de alertas',
                saveAlerts: 'No se pudo guardar la configuración de alertas'
            },
            alerts: {
                title: 'Configurar alertas',
                loading: 'Cargando configuración de alertas...',
                languageLabel: 'Idioma de la comunicación',
                languageHelp: 'Se usará en los correos, en los mensajes de Telegram y en el bloque localizado de los webhooks de este monitor.',
                emailEnabled: 'Enviar alertas por correo',
                emailRecipients: 'Destinatarios por email',
                addEmail: 'Añadir email',
                removeEmail: 'Eliminar destinatario',
                emailHelp: 'Puedes añadirlos uno a uno o pegar varios separados por coma, punto y coma o salto de línea.',
                telegramEnabled: 'Enviar alertas por Telegram',
                telegramChatIds: 'Chats de Telegram',
                telegramHelp: 'Añade uno o varios chat IDs o usuarios de canal separados por coma, punto y coma o salto de línea. El bot debe tener acceso al destino.',
                webhookEnabled: 'Enviar alertas por webhook',
                webhookUrl: 'URL del webhook',
                notificationScopeLabel: 'Tipo de notificaciones',
                notificationScopeHelp: 'Por defecto, este canal silencia las incidencias detectadas como bloqueos de LaLiga.',
                notificationScopeAll: 'Todas las notificaciones',
                notificationScopeExcludeLaLiga: 'Todas excepto incidencias de LaLiga',
                reminderMinutes: 'Recordatorio (minutos)',
                reminderHelp: 'Usa 0 para desactivar recordatorios periódicos.',
                recipientsPlaceholder: 'ops@midominio.com',
                telegramPlaceholder: '-1001234567890, @mi_canal',
                webhookPlaceholder: 'https://hooks.midominio.com/uptime',
                save: 'Guardar alertas'
            },
            cards: {
                currentStatus: 'Estado Actual',
                lastCheck: 'Última comprobación',
                interval: 'Intervalo'
            },
            technical: {
                title: 'Datos técnicos',
                sslTitle: 'Certificado SSL',
                sslDescription: 'Información detectada durante la última inspección TLS.',
                cdnTitle: 'CDN',
                cdnDescription: 'Proveedor inferido a partir de las cabeceras HTTP.',
                subject: 'Sujeto',
                issuer: 'Emisor',
                validUntil: 'Válido hasta',
                checkedAt: 'Comprobado',
                provider: 'Proveedor',
                error: 'Error',
                notDetected: 'No detectado',
                pending: 'Pendiente',
                sslDaysRemaining: 'Caduca en {days} días',
                sslExpiresToday: 'Caduca hoy',
                sslExpired: 'Certificado caducado',
                sslError: 'Certificado con error'
            },
            descriptions: {
                paused: 'El monitor está pausado. La última comprobación se conserva como referencia histórica.',
                up: 'La última comprobación completada fue correcta.',
                laligaBlocked: 'Se ha detectado un bloqueo asociado a LaLiga. El acceso puede fallar solo para parte de los usuarios según su ISP.',
                downAndLaLigaBlocked: 'El monitor está caído y además se ha detectado un bloqueo asociado a LaLiga. Ambos estados pueden coexistir.',
                down: 'La última comprobación completada falló.',
                unknown: 'Todavía no hay un estado concluyente de la última comprobación.'
            },
            never: 'Nunca',
            uptimeHistory: 'Historial de disponibilidad (90 días)',
            ninetyDaysAgo: 'Hace 90 días',
            today: 'Hoy',
            noData: 'Sin datos',
            trendTitle: 'Tendencia de disponibilidad (%)',
            uptimeEditor: {
                title: 'Ajustar disponibilidad de {date}',
                description: 'Modifica manualmente el porcentaje de disponibilidad de este día. El cambio actualiza el resumen de 24h, 7d, 30d y 90d.',
                percentageLabel: 'Porcentaje de disponibilidad',
                percentageHelp: 'Introduce un valor entre 0 y 100 con hasta tres decimales.',
                setPerfect: 'Poner al 100%',
                reset: 'Restablecer',
                resetting: 'Restableciendo...',
                resetAll: 'Restablecer ajustes manuales',
                resettingAll: 'Restableciendo ajustes...'
            },
            uptimeTooltip: {
                exactPercentage: 'Porcentaje exacto',
                realChecks: 'Checks reales',
                averageWeight: 'Peso en promedio',
                manualOverride: 'Ajuste manual',
                manualOverrideYes: 'Sí',
                manualOverrideNo: 'No'
            },
            incidentsTitle: 'Historial de Incidentes',
            noIncidents: 'No hay incidentes registrados',
            incidentOpen: 'Incidente activo',
            incidentResolved: 'Incidente resuelto',
            incidentDismissed: 'Incidente descartado',
            incidentLaLiga: 'Bloqueo LaLiga',
            incidentDuration: 'Duración: {seconds}s',
            dismissedFromPublic: 'Oculta en la vista pública',
            recentChecks: 'Comprobaciones recientes',
            checkOk: 'OK',
            checkFail: 'Fallo',
            resetModalTitle: 'Resetear historial del monitor',
            resetModalDescription: 'Esto borrará la última comprobación visible, el historial de comprobaciones, los incidentes y las alertas generadas para este monitor.',
            resetModalState: 'El monitor quedará con estado {status} hasta la siguiente comprobación.',
            confirmReset: 'Confirmar reset'
        }
    },
    en: {
        app: {
            loading: 'Loading...'
        },
        common: {
            appName: 'Uptime Status',
            loading: 'Loading...',
            loadingDetails: 'Loading details...',
            cancel: 'Cancel',
            save: 'Save',
            saving: 'Saving...',
            delete: 'Delete',
            menu: 'Menu',
            accept: 'Accept',
            back: 'Back',
            close: 'Close',
            expand: 'Expand',
            collapse: 'Collapse',
            logout: 'Log out',
            edit: 'Edit',
            dismiss: 'Dismiss',
            dismissing: 'Dismissing...',
            create: 'Create',
            new: 'New',
            total: 'Total',
            error: 'Error',
            userFallback: 'User',
            noData: 'No data',
            status: {
                operational: 'Operational',
                down: 'Down',
                regionalIssue: 'LaLiga block',
                unknown: 'Unknown',
                paused: 'Paused',
                open: 'Open',
                resolved: 'Resolved',
                dismissed: 'Dismissed',
                acknowledged: 'Acknowledged',
                public: 'Public',
                private: 'Private'
            },
            language: {
                label: 'Language'
            },
            pagination: {
                previous: 'Previous',
                next: 'Next',
                page: 'Page {page} of {total}',
                results: '{start}-{end} of {total}'
            },
            durations: {
                lessThanMinute: 'Less than 1 min',
                daysShort: 'd',
                hoursShort: 'h',
                minutesShort: 'min'
            }
        },
        layout: {
            nav: {
                dashboard: 'Dashboard',
                incidents: 'Incidents',
                statusPages: 'Status pages'
            }
        },
        login: {
            title: 'Sign in',
            subtitle: 'Access your monitors and status pages.',
            emailLabel: 'Email',
            passwordLabel: 'Password',
            emailPlaceholder: 'admin@localhost',
            passwordPlaceholder: 'Your password',
            submit: 'Sign in',
            submitting: 'Signing in...',
            error: 'Could not sign in',
            noAccount: 'Do not have an account?',
            createAccount: 'Create account'
        },
        register: {
            disabledTitle: 'Registration disabled',
            disabledDescription: 'New user sign-ups are disabled in this environment.',
            backToLogin: 'Back to sign in',
            title: 'Create account',
            subtitle: 'Set up your access to manage monitors.',
            fullNameLabel: 'Full name',
            fullNamePlaceholder: 'Your name',
            emailLabel: 'Email',
            emailPlaceholder: 'you@email.com',
            passwordLabel: 'Password',
            passwordPlaceholder: 'Minimum 6 characters',
            submit: 'Create account',
            submitting: 'Creating account...',
            error: 'Could not create the account',
            hasAccount: 'Already have an account?',
            signIn: 'Sign in'
        },
        incidents: {
            title: 'Incidents',
            subtitle: 'All incidents across any of your monitors, sorted from newest to oldest.',
            cards: {
                total: 'Total',
                open: 'Open',
                resolved: 'Resolved'
            },
            historyTitle: 'Global history',
            loading: 'Loading incidents...',
            empty: 'There are no incidents registered for any of your monitors.',
            loadError: 'Could not load incidents',
            monitorLabel: 'Monitor',
            startLabel: 'Start',
            endLabel: 'End',
            durationLabel: 'Duration',
            noDetails: 'No additional details',
            viewMonitor: 'View monitor',
            dismissError: 'Could not dismiss the incident',
            hiddenFromPublic: 'Hidden from the public view'
        },
        statusPageIncidents: {
            loading: 'Loading incidents...',
            loadError: 'Could not load incidents',
            backToStatusPages: 'Back to status pages',
            title: 'Incidents: {name}',
            subtitle: 'Complete incident list for the page, not grouped by service and sorted from newest to oldest.',
            openPublicPage: 'Open public page',
            backToConfig: 'Back to configuration',
            cards: {
                total: 'Total',
                open: 'Open',
                resolved: 'Resolved'
            },
            historyTitle: 'Incident history',
            startLabel: 'Start',
            endLabel: 'End',
            durationLabel: 'Duration',
            noDetails: 'No additional details',
            empty: 'There are no incidents recorded for this page.'
        },
        publicStatusPage: {
            notFound: 'Status page not found or not public',
            serviceStatus: 'Service status',
            pageStatus: 'Page status',
            lastUpdated: 'Last updated {time}',
            pageRefreshedAt: 'Page refreshed {time}',
            autoRefresh: 'Auto-refresh every {seconds}s',
            liveUpdates: 'Live updates enabled',
            summary: {
                hasDown: 'Some services have incidents',
                hasRegionalIssues: 'Some services have LaLiga blocks',
                allUp: 'All systems operational',
                partial: 'Partial service status'
            },
            services: 'Services',
            monitorsCount: '{count} monitors',
            ungrouped: 'Ungrouped',
            uptime: 'Uptime',
            viewDetails: 'View details',
            laligaWarningHelp: 'Regionally affected access: availability may depend on the visitor\'s ISP.',
            poweredBy: 'Powered by Uptime Status'
        },
        publicMonitorDetail: {
            notFound: 'Service not found on this status page',
            backToStatus: 'Back to list',
            checkedEvery: 'Checked every {minutes} minutes',
            uptimeTitle: 'Uptime',
            overallUptime: 'Overall Uptime',
            responseTimeTitle: 'Response Time',
            recentEvents: 'Recent events',
            avgResponseTime: 'Avg. response time',
            maxResponseTime: 'Max. response time',
            minResponseTime: 'Min. response time',
            last24Hours: 'Last 24 hours',
            last7Days: 'Last 7 days',
            last30Days: 'Last 30 days',
            last90Days: 'Last 90 days',
            last2Days: 'Last 2 days',
            noResponseData: 'There is no response time data for this period',
            noEvents: 'There are no recent events for this service',
            reasonWas: 'The reason was {reason}.',
            events: {
                runningAgain: 'Running again',
                currentIssue: 'Service issue',
                downtime: 'Down for {duration}',
                openSince: 'Open since {time}'
            }
        },
        modal: {
            close: 'Close'
        },
        dashboard: {
            status: {
                paused: 'Paused',
                operational: 'Operational',
                down: 'Down',
                laligaBlocked: 'LaLiga block',
                unknown: 'Unknown'
            },
            laligaWarningHelp: 'Regional warning: some users may access it while others may not, depending on their ISP.',
            errors: {
                saveMonitor: 'Could not save the monitor',
                deleteMonitor: 'Error deleting monitor: {error}',
                reorderPanel: 'Could not reorder the dashboard',
                updateStatus: 'Could not update the monitor state',
                duplicateNameMustDiffer: 'When duplicating, the name must differ from the original monitor.',
                duplicateUrlMustDiffer: 'When duplicating, the URL must differ from the original monitor.',
                bulkUpdate: 'Could not apply the bulk changes.',
                bulkDelete: 'Could not delete the selected monitors.',
                bulkFieldsRequired: 'Select at least one field to update.',
                bulkIntervalInvalid: 'Interval must be a number greater than or equal to 60 seconds.',
                bulkTimeoutInvalid: 'Timeout must be a number greater than or equal to 1 second.',
                bulkExpectedCodeInvalid: 'Expected code must be between 100 and 599.',
                bulkTcpPortInvalid: 'TCP port must be between 1 and 65535.'
            },
            title: 'Dashboard',
            subtitle: 'Real-time monitoring for your services',
            newMonitor: 'Add monitor',
            cards: {
                total: 'Total',
                operational: 'Operational',
                down: 'Down',
                blocked: 'LaLiga blocks',
                paused: 'Paused'
            },
            modal: {
                editTitle: 'Edit monitor',
                newTitle: 'New monitor',
                duplicateTitle: 'Duplicate monitor'
            },
            sections: {
                whatTitle: 'What to monitor',
                whatDescription: 'Define the service and the exact endpoint that the check should reach.',
                frequencyTitle: 'Check frequency',
                frequencyDescription: 'Set how often the service is checked and how long to wait before marking it as failed.',
                validationTitle: 'Validation rules',
                validationDescription: 'Refine how a successful check is decided depending on the monitor type.',
                listTitle: 'Configured monitors',
                listDescription: 'You can move each monitor to change its position on the dashboard.'
            },
            fields: {
                name: 'Name',
                group: 'Group',
                type: 'Type',
                intervalSeconds: 'Interval (sec)',
                timeoutSeconds: 'Timeout (sec)',
                httpMethod: 'HTTP method',
                expectedCode: 'Expected code',
                expectedKeyword: 'Expected keyword',
                tcpPort: 'TCP port',
                host: 'Host',
                hostOrIp: 'Host or IP',
                url: 'URL'
            },
            placeholders: {
                apiName: 'Example: Main API',
                group: 'Example: Core, Frontend, Infrastructure',
                host: 'db.example.com',
                hostOrIp: '8.8.8.8',
                urlHttp: 'http://api.example.com/health',
                urlHttps: 'https://api.example.com/health',
                expectedCode: '200',
                expectedKeyword: 'healthy',
                tcpPort: '443',
                bulkClearable: 'Leave blank to clear this value'
            },
            helps: {
                name: 'This is the name shown on the dashboard and in incidents.',
                group: 'Use a shared name to organize related monitors on the dashboard and public status page.',
                type: 'Only change the check type; the rest of the form adapts automatically.',
                host: 'Provide only the host. The port is configured in the TCP field.',
                hostOrIp: 'Use a host or IP address that should reply to ping.',
                url: 'Use the exact URL that should answer the check.',
                intervalSeconds: 'Minimum 60 seconds. Use higher values for low-priority services.',
                timeoutSeconds: 'Maximum wait time before the check is considered unresponsive.',
                httpMethod: 'GET is usually the right option for status or health endpoints.',
                expectedCode: 'Optional. If left empty, any HTTP code will be evaluated together with the rest of the check.',
                expectedKeyword: 'Optional. It will only be marked OK if the response contains this text.',
                tcpPort: 'Provide the port that should accept connections from the worker.',
                ping: 'For a Ping monitor you only need the host or IP. There are no additional validation rules.'
            },
            summary: {
                noFixedCode: 'no fixed code',
                contains: 'contains "{keyword}"',
                tcpConnection: 'TCP connection on port {port}',
                tcpNoPort: 'TCP connection without a defined port',
                ping: 'Ping connectivity check'
            },
            bulkEdit: {
                title: 'Edit {count} monitors',
                organizationTitle: 'Organization',
                organizationDescription: 'Assign a common group or clear the group from the selected monitors.',
                frequencyTitle: 'Frequency and timeout',
                frequencyDescription: 'Enable only the fields you want to apply to all selected monitors.',
                validationTitle: 'Validation rules',
                validationDescription: 'If you enable a field and leave it blank, it will be cleared for all selected monitors.',
                blankClearsField: 'If you enable this field and leave it blank, it will be removed from all selected monitors.',
                mixedSelectionHelp: 'The selection mixes different monitor types. Bulk editing only shows validation fields compatible with all of them.',
                noValidationFields: 'This selection does not have shared validation fields that can be edited in bulk.'
            },
            filters: {
                toggle: 'Filters',
                search: 'Search',
                searchPlaceholder: 'Name, URL, group or CDN',
                status: 'Status',
                allStatuses: 'All statuses',
                active: 'Active',
                notifications: 'Notifications',
                allNotifications: 'All notifications',
                withNotifications: 'With alerts',
                withoutNotifications: 'Without alerts',
                emailNotifications: 'Email only',
                telegramNotifications: 'Telegram only',
                webhookNotifications: 'Webhook only',
                allTypes: 'All types',
                allGroups: 'All groups',
                cdn: 'CDN',
                allCdns: 'All CDNs',
                withoutCdn: 'No CDN',
                results: '{shown} of {total}',
                clear: 'Clear',
                noResults: 'No monitors match the selected filters.'
            },
            actions: {
                saveChanges: 'Save changes',
                createMonitor: 'Create monitor',
                duplicateMonitor: 'Duplicate monitor',
                alertsSelected: 'Alerts',
                editSelected: 'Edit',
                pauseSelected: 'Pause',
                resumeSelected: 'Resume',
                deleteSelected: 'Delete',
                applyBulkEdit: 'Apply changes',
                selectAll: 'Select all',
                clearSelection: 'Clear selection',
                selectedCount: '{count} selected',
                selectMonitor: 'Select monitor {name}',
                moveUp: 'Move up',
                moveDown: 'Move down',
                pauseMonitor: 'Pause monitor',
                resumeMonitor: 'Resume monitor',
                checkEvery: 'Every {minutes} min',
                checkStopped: 'Check stopped',
                unchecked: 'Unchecked'
            },
            loading: 'Loading...',
            empty: 'There are no configured monitors. Create one to get started.',
            deleteTitle: 'Delete monitor',
            deletePrompt: 'Delete the monitor "{name}" permanently?',
            bulkDeleteTitle: 'Delete {count} monitors',
            bulkDeletePrompt: 'Permanently delete the {count} selected monitors? This action cannot be undone.',
            bulkAlertsTitle: 'Alerts for {count} monitors',
            bulkAlertsDescription: 'This configuration will replace the alert settings of all selected monitors.',
            bulkAlertsSave: 'Could not save bulk alert settings.',
            bulkAlertsPrefillConfigured: 'Shared values were preloaded from {configured} monitors with saved preferences.',
            bulkAlertsPrefillDefaults: 'None of the {total} selected monitors have saved alert preferences yet. The modal starts from the default configuration.',
            bulkAlertsPrefillConflicts: 'Several different saved configurations were found. Choose below which one you want to load as the base before applying it to all selected monitors.',
            bulkAlertsSourceLabel: 'Base configuration',
            bulkAlertsSourceHelp: 'You can load one of the detected saved configurations into the form and still adjust it afterwards before applying it to the whole selection.',
            bulkAlertsSourceShared: 'Keep only the shared values',
            bulkAlertsSourceCustom: 'Manually adjusted values',
            bulkAlertsSourceMoreMonitors: '{name} and {count} more',
            groups: {
                ungrouped: 'Ungrouped',
                manage: 'Groups',
                manageTitle: 'Manage groups',
                manageDescription: 'Add, rename or delete groups. Deleting a group leaves its monitors ungrouped.',
                empty: 'There are no groups yet.',
                createTitle: 'Assign monitor to group',
                create: 'Assign group',
                createHelp: 'The selected monitor is moved to the specified group. If the group does not exist, it is created when assigned.',
                assignTo: 'Monitor',
                noMonitors: 'No monitors available',
                newName: 'Group name',
                insertTyped: 'Use "{name}"',
                rename: 'Rename',
                clear: 'Delete group',
                nameRequired: 'Group name cannot be empty.',
                monitorRequired: 'Select the monitor you want to assign or move.',
                updateError: 'Could not update the group.'
            },
            technical: {
                cdn: 'CDN',
                sslDays: '{days}d',
                sslExpired: 'Expired',
                sslError: 'Error'
            }
        },
        statusPages: {
            errors: {
                savePage: 'Could not save the status page',
                deletePage: 'Error deleting: {error}',
                loadDetail: 'Error loading details:',
                loadPage: 'Error loading status page: {error}'
            },
            title: 'Status pages',
            subtitle: 'Configure public status pages',
            newPage: 'New status page',
            modal: {
                editTitle: 'Edit status page',
                newTitle: 'New status page'
            },
            sections: {
                identityTitle: 'Public identity',
                identityDescription: 'Configure the name and URL that users will see for this page.',
                publicationTitle: 'Publication and styling',
                publicationDescription: 'Adjust whether the page is visible and the basic visual information to show.',
                monitorsTitle: 'Visible monitors',
                monitorsDescription: 'Choose which monitors are part of this page and in which order they appear.',
                orderTitle: 'Public page order',
                orderDescription: 'The first monitor in the list will appear at the very top of the public page.'
            },
            fields: {
                slug: 'Slug (URL)',
                name: 'Name',
                description: 'Description',
                logoUrl: 'Logo URL',
                monitorSearch: 'Search monitors',
                theme: 'Theme',
                public: 'Public'
            },
            placeholders: {
                slug: 'my-service',
                name: 'My Service Status',
                description: 'Public status page...',
                logoUrl: 'https://...',
                monitorSearch: 'Search by name, URL or group',
                previewSlug: 'my-service'
            },
            helps: {
                slug: 'Used in the public URL. Keep it short, stable and without spaces.',
                name: 'Optional. If left empty, the public page will show only the logo and the rest of the content.',
                description: 'Optional. Use it to explain which services this page covers or who it is for.',
                logoUrl: 'Use a lightweight public image, such as the service logo.',
                theme: 'This only affects the look of the public page.',
                public: 'If disabled, the page still exists but will not be shown as a public page.',
                noMonitors: 'You can save the page without monitors, but it will not show operational status until you select at least one.',
                noSearchResults: 'No monitors match the current search.',
                selectedCount: '{count} selected',
                orderItem: '{index}. {label}',
                urlPreview: 'URL preview'
            },
            themes: {
                light: 'Light',
                dark: 'Dark'
            },
            actions: {
                selectAll: 'Select all',
                clear: 'Clear',
                saveChanges: 'Save changes',
                createPage: 'Create status page',
                moveUp: 'Move up',
                moveDown: 'Move down',
                viewPublic: 'View public page',
                viewIncidents: 'View incidents',
                openPublicPage: 'Open public page'
            },
            listTitle: 'My status pages',
            loading: 'Loading...',
            empty: 'There are no status pages yet',
            detailTitle: 'Details: {name}',
            detailSubtitle: 'Operational summary and visible configuration for the public page.',
            cards: {
                visibility: 'Visibility',
                effectiveUrl: 'Effective URL',
                monitors: 'Monitors',
                description: 'Description',
                unavailable: 'Unavailable',
                internalRoute: 'Internal public route',
                assigned: '{count} assigned',
                statusSummary: '{up} operational, {down} down',
                noDescription: 'No description configured',
                originName: 'Source name',
                lastCheck: 'Last check',
                noLastCheck: 'No data',
                noMonitors: 'There are no assigned monitors.'
            },
            deleteTitle: 'Delete status page',
            deletePrompt: 'Delete the status page "{name}"?',
            publicRoute: 'Internal public route'
        },
        monitorDetail: {
            loading: 'Loading details...',
            notFound: 'Monitor not found',
            status: {
                paused: 'Paused',
                operational: 'Operational',
                down: 'Down',
                laligaBlocked: 'LaLiga block',
                unknown: 'Unknown'
            },
            buttons: {
                alerts: 'Alerts',
                resetHistory: 'Reset history',
                resetting: 'Resetting...',
                pauseMonitor: 'Pause monitor',
                resumeMonitor: 'Resume monitor',
                updating: 'Updating...'
            },
            errors: {
                updateStatus: 'Could not update the monitor state',
                resetHistory: 'Could not reset the monitor history',
                dismissIncident: 'Could not dismiss the incident',
                saveUptimeOverride: 'Could not save the manual uptime adjustment',
                resetUptimeOverride: 'Could not reset the manual uptime adjustment',
                resetUptimeOverrides: 'Could not reset the manual uptime adjustments',
                loadAlerts: 'Could not load alert settings',
                saveAlerts: 'Could not save alert settings'
            },
            alerts: {
                title: 'Configure alerts',
                loading: 'Loading alert settings...',
                languageLabel: 'Communication language',
                languageHelp: 'It will be used for emails, Telegram messages, and for the localized block of this monitor webhook payloads.',
                emailEnabled: 'Send email alerts',
                emailRecipients: 'Email recipients',
                addEmail: 'Add email',
                removeEmail: 'Remove recipient',
                emailHelp: 'You can add them one by one or paste several separated by comma, semicolon or line break.',
                telegramEnabled: 'Send Telegram alerts',
                telegramChatIds: 'Telegram chats',
                telegramHelp: 'Add one or more chat IDs or channel usernames separated by comma, semicolon or line break. The bot must be able to reach that destination.',
                webhookEnabled: 'Send webhook alerts',
                webhookUrl: 'Webhook URL',
                notificationScopeLabel: 'Notification scope',
                notificationScopeHelp: 'By default, this channel mutes incidents detected as LaLiga blocks.',
                notificationScopeAll: 'All notifications',
                notificationScopeExcludeLaLiga: 'All except LaLiga incidents',
                reminderMinutes: 'Reminder (minutes)',
                reminderHelp: 'Use 0 to disable periodic reminders.',
                recipientsPlaceholder: 'ops@yourdomain.com',
                telegramPlaceholder: '-1001234567890, @my_channel',
                webhookPlaceholder: 'https://hooks.yourdomain.com/uptime',
                save: 'Save alerts'
            },
            cards: {
                currentStatus: 'Current status',
                lastCheck: 'Last check',
                interval: 'Interval'
            },
            technical: {
                title: 'Technical data',
                sslTitle: 'SSL certificate',
                sslDescription: 'Information detected during the latest TLS inspection.',
                cdnTitle: 'CDN',
                cdnDescription: 'Provider inferred from the HTTP response headers.',
                subject: 'Subject',
                issuer: 'Issuer',
                validUntil: 'Valid until',
                checkedAt: 'Checked at',
                provider: 'Provider',
                error: 'Error',
                notDetected: 'Not detected',
                pending: 'Pending',
                sslDaysRemaining: 'Expires in {days} days',
                sslExpiresToday: 'Expires today',
                sslExpired: 'Certificate expired',
                sslError: 'Certificate error'
            },
            descriptions: {
                paused: 'The monitor is paused. The last check is kept as historical reference.',
                up: 'The last completed check was successful.',
                laligaBlocked: 'A LaLiga-related block has been detected. Availability may fail only for some users depending on their ISP.',
                downAndLaLigaBlocked: 'The monitor is down and a LaLiga-related block has also been detected. Both states can coexist.',
                down: 'The last completed check failed.',
                unknown: 'There is no conclusive result for the last check yet.'
            },
            never: 'Never',
            uptimeHistory: 'Availability history (90 days)',
            ninetyDaysAgo: '90 days ago',
            today: 'Today',
            noData: 'No data',
            trendTitle: 'Availability trend (%)',
            uptimeEditor: {
                title: 'Adjust availability for {date}',
                description: 'Manually change the uptime percentage for this day. The change updates the 24h, 7d, 30d, and 90d summaries.',
                percentageLabel: 'Availability percentage',
                percentageHelp: 'Enter a value between 0 and 100 with up to three decimal places.',
                setPerfect: 'Set to 100%',
                reset: 'Reset',
                resetting: 'Resetting...',
                resetAll: 'Reset manual adjustments',
                resettingAll: 'Resetting adjustments...'
            },
            uptimeTooltip: {
                exactPercentage: 'Exact percentage',
                realChecks: 'Real checks',
                averageWeight: 'Average weight',
                manualOverride: 'Manual override',
                manualOverrideYes: 'Yes',
                manualOverrideNo: 'No'
            },
            incidentsTitle: 'Incident history',
            noIncidents: 'There are no recorded incidents',
            incidentOpen: 'Active incident',
            incidentResolved: 'Resolved incident',
            incidentDismissed: 'Dismissed incident',
            incidentLaLiga: 'LaLiga block',
            incidentDuration: 'Duration: {seconds}s',
            dismissedFromPublic: 'Hidden from the public view',
            recentChecks: 'Recent checks',
            checkOk: 'OK',
            checkFail: 'Failure',
            resetModalTitle: 'Reset monitor history',
            resetModalDescription: 'This will delete the visible last check, check history, incidents and generated alerts for this monitor.',
            resetModalState: 'The monitor will remain in {status} state until the next check.',
            confirmReset: 'Confirm reset'
        }
    },
    eu: {
        app: {
            loading: 'Kargatzen...'
        },
        common: {
            appName: 'Uptime Status',
            loading: 'Kargatzen...',
            loadingDetails: 'Xehetasunak kargatzen...',
            cancel: 'Utzi',
            save: 'Gorde',
            saving: 'Gordetzen...',
            delete: 'Ezabatu',
            menu: 'Menua',
            accept: 'Onartu',
            back: 'Itzuli',
            close: 'Itxi',
            expand: 'Zabaldu',
            collapse: 'Tolestu',
            logout: 'Irten',
            edit: 'Editatu',
            dismiss: 'Baztertu',
            dismissing: 'Baztertzen...',
            create: 'Sortu',
            new: 'Berria',
            total: 'Guztira',
            error: 'Errorea',
            userFallback: 'Erabiltzailea',
            noData: 'Daturik ez',
            status: {
                operational: 'Martxan',
                down: 'Erorita',
                regionalIssue: 'LaLigako blokeoa',
                unknown: 'Ezezaguna',
                paused: 'Pausatuta',
                open: 'Irekia',
                resolved: 'Konponduta',
                dismissed: 'Baztertua',
                acknowledged: 'Aitortua',
                public: 'Publikoa',
                private: 'Pribatua'
            },
            language: {
                label: 'Hizkuntza'
            },
            pagination: {
                previous: 'Aurrekoa',
                next: 'Hurrengoa',
                page: '{page}. orria / {total}',
                results: '{start}-{end} / {total}'
            },
            durations: {
                lessThanMinute: 'Minutu 1 baino gutxiago',
                daysShort: 'e',
                hoursShort: 'h',
                minutesShort: 'min'
            }
        },
        layout: {
            nav: {
                dashboard: 'Panela',
                incidents: 'Gorabeherak',
                statusPages: 'Egoera orriak'
            }
        },
        login: {
            title: 'Saioa hasi',
            subtitle: 'Sartu zure monitore eta egoera orrietara.',
            emailLabel: 'Posta elektronikoa',
            passwordLabel: 'Pasahitza',
            emailPlaceholder: 'admin@localhost',
            passwordPlaceholder: 'Zure pasahitza',
            submit: 'Sartu',
            submitting: 'Saioa hasten...',
            error: 'Ezin izan da saioa hasi',
            noAccount: 'Ez duzu konturik?',
            createAccount: 'Sortu kontua'
        },
        register: {
            disabledTitle: 'Erregistroa desgaituta',
            disabledDescription: 'Ingurune honetan ez da erabiltzaile berririk onartzen.',
            backToLogin: 'Itzuli sarbidera',
            title: 'Sortu kontua',
            subtitle: 'Prestatu zure sarbidea monitoreak kudeatzeko.',
            fullNameLabel: 'Izen osoa',
            fullNamePlaceholder: 'Zure izena',
            emailLabel: 'Posta elektronikoa',
            emailPlaceholder: 'zu@email.com',
            passwordLabel: 'Pasahitza',
            passwordPlaceholder: 'Gutxienez 6 karaktere',
            submit: 'Sortu kontua',
            submitting: 'Kontua sortzen...',
            error: 'Ezin izan da kontua sortu',
            hasAccount: 'Baduzu konturik?',
            signIn: 'Saioa hasi'
        },
        incidents: {
            title: 'Gorabeherak',
            subtitle: 'Zure monitore guztietako gorabehera guztiak, berrienetik zaharrenera ordenatuta.',
            cards: {
                total: 'Guztira',
                open: 'Irekiak',
                resolved: 'Konponduta'
            },
            historyTitle: 'Historia orokorra',
            loading: 'Gorabeherak kargatzen...',
            empty: 'Ez dago gorabeherarik zure monitoreetan.',
            loadError: 'Ezin izan dira gorabeherak kargatu',
            monitorLabel: 'Monitorea',
            startLabel: 'Hasiera',
            endLabel: 'Amaiera',
            durationLabel: 'Iraupena',
            noDetails: 'Xehetasun gehigarririk ez',
            viewMonitor: 'Ikusi monitorea',
            dismissError: 'Ezin izan da gorabehera baztertu',
            hiddenFromPublic: 'Ikuspegi publikotik ezkutatuta'
        },
        statusPageIncidents: {
            loading: 'Gorabeherak kargatzen...',
            loadError: 'Ezin izan dira gorabeherak kargatu',
            backToStatusPages: 'Itzuli egoera orrietara',
            title: 'Gorabeherak: {name}',
            subtitle: 'Orri honetako gorabehera zerrenda osoa, zerbitzuka multzokatu gabe eta berrienetik zaharrenera ordenatuta.',
            openPublicPage: 'Ireki orri publikoa',
            backToConfig: 'Itzuli konfiguraziora',
            cards: {
                total: 'Guztira',
                open: 'Irekiak',
                resolved: 'Konponduta'
            },
            historyTitle: 'Gorabeheren historia',
            startLabel: 'Hasiera',
            endLabel: 'Amaiera',
            durationLabel: 'Iraupena',
            noDetails: 'Xehetasun gehigarririk ez',
            empty: 'Ez dago gorabeherarik orri honetarako.'
        },
        publicStatusPage: {
            notFound: 'Egoera orria ez da aurkitu edo ez da publikoa',
            serviceStatus: 'Zerbitzuaren egoera',
            pageStatus: 'Orriaren egoera',
            lastUpdated: 'Azken eguneraketa {time}',
            pageRefreshedAt: 'Orria eguneratu da {time}',
            autoRefresh: '{seconds} s-ro automatikoki eguneratzen da',
            liveUpdates: 'Eguneratzeak denbora errealean',
            summary: {
                hasDown: 'Zerbitzu batzuek gorabeherak dituzte',
                hasRegionalIssues: 'Zerbitzu batzuek LaLigako blokeoak dituzte',
                allUp: 'Sistema guztiak martxan',
                partial: 'Zerbitzuaren egoera partziala'
            },
            services: 'Zerbitzuak',
            monitorsCount: '{count} monitore',
            ungrouped: 'Talderik gabe',
            uptime: 'Uptime',
            viewDetails: 'Ikusi xehetasuna',
            laligaWarningHelp: 'Sarbidea eskualdeka eraginpean egon daiteke: bisitariaren ISP-aren araberakoa izan daiteke.',
            poweredBy: 'Uptime Status-ekin egina'
        },
        publicMonitorDetail: {
            notFound: 'Zerbitzua ez da egoera orri honetan aurkitu',
            backToStatus: 'Itzuli zerrendara',
            checkedEvery: '{minutes} minuturo egiaztatua',
            uptimeTitle: 'Uptime',
            overallUptime: 'Uptime orokorra',
            responseTimeTitle: 'Erantzun denbora',
            recentEvents: 'Azken gertaerak',
            avgResponseTime: 'Batez besteko erantzun denbora',
            maxResponseTime: 'Gehieneko erantzun denbora',
            minResponseTime: 'Gutxieneko erantzun denbora',
            last24Hours: 'Azken 24 orduak',
            last7Days: 'Azken 7 egunak',
            last30Days: 'Azken 30 egunak',
            last90Days: 'Azken 90 egunak',
            last2Days: 'Azken 2 egunak',
            noResponseData: 'Ez dago epe honetarako erantzun denboraren daturik',
            noEvents: 'Ez dago zerbitzu honetarako azken gertaerarik',
            reasonWas: 'Arrazoia hau izan da: {reason}.',
            events: {
                runningAgain: 'Berriro martxan',
                currentIssue: 'Gorabehera aktiboa',
                downtime: '{duration}z erorita',
                openSince: '{time}-tik irekita'
            }
        },
        modal: {
            close: 'Itxi'
        },
        dashboard: {
            status: {
                paused: 'Pausatuta',
                operational: 'Martxan',
                down: 'Erorita',
                laligaBlocked: 'LaLigako blokeoa',
                unknown: 'Ezezaguna'
            },
            laligaWarningHelp: 'Eskualdeko abisua: erabiltzaile batzuek sartu ahal izango dute eta beste batzuek ez, beren ISP-aren arabera.',
            errors: {
                saveMonitor: 'Ezin izan da monitorea gorde',
                deleteMonitor: 'Errorea monitorea ezabatzean: {error}',
                reorderPanel: 'Ezin izan da panela berrantolatu',
                updateStatus: 'Ezin izan da monitoreren egoera eguneratu',
                duplicateNameMustDiffer: 'Bikoiztean, izena jatorrizko monitorerena baino desberdina izan behar da.',
                duplicateUrlMustDiffer: 'Bikoiztean, URLa jatorrizko monitorerena baino desberdina izan behar da.',
                bulkUpdate: 'Ezin izan dira multzoko aldaketak aplikatu.',
                bulkDelete: 'Ezin izan dira hautatutako monitoreak ezabatu.',
                bulkFieldsRequired: 'Hautatu eguneratzeko gutxienez eremu bat.',
                bulkIntervalInvalid: 'Tarteak 60 segundo edo gehiagoko zenbaki bat izan behar du.',
                bulkTimeoutInvalid: 'Timeout-ak 1 segundo edo gehiagoko zenbaki bat izan behar du.',
                bulkExpectedCodeInvalid: 'Espero den kodeak 100 eta 599 artekoa izan behar du.',
                bulkTcpPortInvalid: 'TCP atakak 1 eta 65535 artekoa izan behar du.'
            },
            title: 'Panela',
            subtitle: 'Zure zerbitzuen denbora errealeko monitorizazioa',
            newMonitor: 'Gehitu monitorea',
            cards: {
                total: 'Guztira',
                operational: 'Martxan',
                down: 'Erorita',
                blocked: 'LaLigako blokeoak',
                paused: 'Pausatuta'
            },
            modal: {
                editTitle: 'Editatu monitorea',
                newTitle: 'Monitore berria',
                duplicateTitle: 'Bikoiztu monitorea'
            },
            sections: {
                whatTitle: 'Zer monitorizatu',
                whatDescription: 'Definitu zerbitzua eta egiaztapenak iritsi behar duen puntua.',
                frequencyTitle: 'Egiaztapen maiztasuna',
                frequencyDescription: 'Aukeratu zenbat maiztasunez berrikusi zerbitzua eta zenbat denbora itxaron huts gisa markatu aurretik.',
                validationTitle: 'Balidazio irizpideak',
                validationDescription: 'Zehaztu noiz hartuko den egiaztapena ontzat monitorizazio motaren arabera.',
                listTitle: 'Konfiguratutako monitoreak',
                listDescription: 'Monitore bakoitza mugitu dezakezu panelean duen posizioa aldatzeko.'
            },
            fields: {
                name: 'Izena',
                group: 'Taldea',
                type: 'Mota',
                intervalSeconds: 'Tartea (seg)',
                timeoutSeconds: 'Timeout-a (seg)',
                httpMethod: 'HTTP metodoa',
                expectedCode: 'Espero den kodea',
                expectedKeyword: 'Espero den hitza',
                tcpPort: 'TCP ataka',
                host: 'Hosta',
                hostOrIp: 'Hosta edo IP',
                url: 'URLa'
            },
            placeholders: {
                apiName: 'Adib. API nagusia',
                group: 'Adib. Core, Frontend, Azpiegitura',
                host: 'db.adibidea.com',
                hostOrIp: '8.8.8.8',
                urlHttp: 'http://api.example.com/health',
                urlHttps: 'https://api.example.com/health',
                expectedCode: '200',
                expectedKeyword: 'healthy',
                tcpPort: '443',
                bulkClearable: 'Utzi hutsik balio hau garbitzeko'
            },
            helps: {
                name: 'Panelan eta gorabeheretan ikusiko duzun izena da.',
                group: 'Erabili izen partekatu bat lotutako monitoreak panelean eta orri publikoan antolatzeko.',
                type: 'Aldatu egiaztapen mota bakarrik; gainerako formularioa automatikoki egokituko da.',
                host: 'Hosta bakarrik eman. Ataka TCP eremuan konfiguratzen da.',
                hostOrIp: 'Erabili ping erantzun behar duen host edo IP bat.',
                url: 'Erabili egiaztapenak erantzun behar duen URL zehatza.',
                intervalSeconds: 'Gutxienez 60 segundo. Garrantzi txikiko zerbitzuetarako balio handiagoak erabili.',
                timeoutSeconds: 'Gehienezko itxaronaldia, erantzunik ez dagoela jotzeko.',
                httpMethod: 'GET da normalean egoera edo osasun endpointentzat aukerarik egokiena.',
                expectedCode: 'Aukerakoa. Hutsik uzten bada, HTTP kode oro gainontzeko egiaztapenarekin batera ebaluatuko da.',
                expectedKeyword: 'Aukerakoa. Erantzunak testu hori badu bakarrik markatuko da OK gisa.',
                tcpPort: 'Worker-ak konektatu behar duen ataka adierazi.',
                ping: 'Ping motako monitore baterako hosta edo IP bakarrik behar duzu. Ez dago balidazio gehigarririk.'
            },
            summary: {
                noFixedCode: 'kode finkorik gabe',
                contains: '"{keyword}" dauka',
                tcpConnection: 'TCP konexioa {port} atakan',
                tcpNoPort: 'TCP konexioa atakarik zehaztu gabe',
                ping: 'Ping konektibitate egiaztapena'
            },
            bulkEdit: {
                title: 'Editatu {count} monitore',
                organizationTitle: 'Antolaketa',
                organizationDescription: 'Esleitu talde komun bat edo garbitu hautatutako monitoreen taldea.',
                frequencyTitle: 'Maiztasuna eta timeout-a',
                frequencyDescription: 'Gaitu hautatutako monitore guztiei aplikatu nahi dizkiegun eremuak soilik.',
                validationTitle: 'Balidazio irizpideak',
                validationDescription: 'Eremu bat gaitzen baduzu eta hutsik uzten baduzu, hautatutako monitore guztietan garbituko da.',
                blankClearsField: 'Eremu hau gaitzen baduzu eta hutsik uzten baduzu, hautatutako monitore guztietatik kenduko da.',
                mixedSelectionHelp: 'Hautaketak monitorizazio mota desberdinak nahasten ditu. Multzoko edizioan guztiekin bateragarriak diren balidazio eremuak bakarrik erakusten dira.',
                noValidationFields: 'Hautaketa honek ez du multzoka editatu daitekeen balidazio eremu komunik.'
            },
            filters: {
                toggle: 'Iragazkiak',
                search: 'Bilatu',
                searchPlaceholder: 'Izena, URLa, taldea edo CDN',
                status: 'Egoera',
                allStatuses: 'Egoera guztiak',
                active: 'Aktiboak',
                notifications: 'Jakinarazpenak',
                allNotifications: 'Jakinarazpen guztiak',
                withNotifications: 'Alertadunak',
                withoutNotifications: 'Alertarik gabe',
                emailNotifications: 'Email bakarrik',
                telegramNotifications: 'Telegram bakarrik',
                webhookNotifications: 'Webhook bakarrik',
                allTypes: 'Mota guztiak',
                allGroups: 'Talde guztiak',
                cdn: 'CDN',
                allCdns: 'CDN guztiak',
                withoutCdn: 'CDN gabe',
                results: '{shown} / {total}',
                clear: 'Garbitu',
                noResults: 'Ez dago iragazkiekin bat datorren monitorerik.'
            },
            actions: {
                saveChanges: 'Gorde aldaketak',
                createMonitor: 'Sortu monitorea',
                duplicateMonitor: 'Bikoiztu monitorea',
                alertsSelected: 'Alertak',
                editSelected: 'Editatu',
                pauseSelected: 'Pausatu',
                resumeSelected: 'Berrekin',
                deleteSelected: 'Ezabatu',
                applyBulkEdit: 'Aplikatu aldaketak',
                selectAll: 'Hautatu guztiak',
                clearSelection: 'Garbitu hautaketa',
                selectedCount: '{count} hautatuta',
                selectMonitor: '{name} monitorea hautatu',
                moveUp: 'Mugitu gora',
                moveDown: 'Mugitu behera',
                pauseMonitor: 'Pausatu monitorea',
                resumeMonitor: 'Berrekin monitori',
                checkEvery: '{minutes} minutuero',
                checkStopped: 'Egiaztapena geldituta',
                unchecked: 'Egiaztatu gabe'
            },
            loading: 'Kargatzen...',
            empty: 'Ez dago konfiguratutako monitorerik. Sortu bat hasteko.',
            deleteTitle: 'Ezabatu monitorea',
            deletePrompt: '"{name}" monitorea betiko ezabatu?',
            bulkDeleteTitle: 'Ezabatu {count} monitore',
            bulkDeletePrompt: 'Betirako ezabatu hautatutako {count} monitoreak? Ekintza hau ezin da desegin.',
            bulkAlertsTitle: '{count} monitoreren alertak',
            bulkAlertsDescription: 'Konfigurazio honek hautatutako monitore guztien alerta ezarpenak ordezkatuko ditu.',
            bulkAlertsSave: 'Ezin izan dira multzoko alertak gorde.',
            bulkAlertsPrefillConfigured: 'Partekatutako balioak gordetako hobespenak dituzten {configured} monitoreetatik aurrekargatu dira.',
            bulkAlertsPrefillDefaults: 'Hautatutako {total} monitoreetatik inork ez du oraindik alerten hobespenik gordeta. Modala lehenetsitako konfiguraziotik hasten da.',
            bulkAlertsPrefillConflicts: 'Gordetako hainbat konfigurazio desberdin aurkitu dira. Aukeratu behean zein kargatu nahi duzun oinarri gisa hautatutako monitore guztiei aplikatu aurretik.',
            bulkAlertsSourceLabel: 'Oinarrizko konfigurazioa',
            bulkAlertsSourceHelp: 'Atzemandako gordetako konfigurazioetako bat karga dezakezu formularioan, eta gero ere doitu, hautaketa osoari aplikatu aurretik.',
            bulkAlertsSourceShared: 'Mantendu partekatutako balioak bakarrik',
            bulkAlertsSourceCustom: 'Eskuz doitutako balioak',
            bulkAlertsSourceMoreMonitors: '{name} eta beste {count}',
            groups: {
                ungrouped: 'Talderik gabe',
                manage: 'Taldeak',
                manageTitle: 'Kudeatu taldeak',
                manageDescription: 'Gehitu, berrizendatu edo ezabatu taldeak. Talde bat ezabatzean, haren monitoreak talderik gabe geratzen dira.',
                empty: 'Oraindik ez dago talderik.',
                createTitle: 'Esleitu monitorea taldeari',
                create: 'Esleitu taldea',
                createHelp: 'Hautatutako monitorea adierazitako taldera mugitzen da. Taldea ez badago, esleitzean sortzen da.',
                assignTo: 'Monitorea',
                noMonitors: 'Ez dago monitorerik erabilgarri',
                newName: 'Taldearen izena',
                insertTyped: 'Erabili "{name}"',
                rename: 'Berrizendatu',
                clear: 'Ezabatu taldea',
                nameRequired: 'Taldearen izena ezin da hutsik egon.',
                monitorRequired: 'Hautatu esleitu edo mugitu nahi duzun monitorea.',
                updateError: 'Ezin izan da taldea eguneratu.'
            },
            technical: {
                cdn: 'CDN',
                sslDays: '{days} e',
                sslExpired: 'Iraungita',
                sslError: 'Errorea'
            }
        },
        statusPages: {
            errors: {
                savePage: 'Ezin izan da egoera orria gorde',
                deletePage: 'Errorea ezabatzean: {error}',
                loadDetail: 'Errorea xehetasunak kargatzean:',
                loadPage: 'Errorea egoera orria kargatzean: {error}'
            },
            title: 'Egoera orriak',
            subtitle: 'Konfiguratu egoera orri publikoak',
            newPage: 'Egoera orri berria',
            modal: {
                editTitle: 'Editatu egoera orria',
                newTitle: 'Egoera orri berria'
            },
            sections: {
                identityTitle: 'Identitate publikoa',
                identityDescription: 'Konfiguratu erabiltzaileek orri honetan ikusiko duten izena eta URLa.',
                publicationTitle: 'Argitalpena eta itxura',
                publicationDescription: 'Doitu orria ikusgai egongo den eta zer oinarrizko datu bisual erakutsi nahi dituzun.',
                monitorsTitle: 'Ikusgai dauden monitoreak',
                monitorsDescription: 'Aukeratu zein monitore agertuko diren orri honetan eta zein ordenatan.',
                orderTitle: 'Orri publikoko ordena',
                orderDescription: 'Zerrendako lehen monitorea orri publikoaren goialdean agertuko da.'
            },
            fields: {
                slug: 'Slug-a (URL)',
                name: 'Izena',
                description: 'Deskribapena',
                logoUrl: 'Logoaren URLa',
                monitorSearch: 'Monitoreak bilatu',
                theme: 'Gaia',
                public: 'Publikoa'
            },
            placeholders: {
                slug: 'nire-zerbitzua',
                name: 'Nire zerbitzuaren egoera',
                description: 'Egoera orri publikoa...',
                logoUrl: 'https://...',
                monitorSearch: 'Bilatu izen, URL edo taldearen arabera',
                previewSlug: 'nire-zerbitzua'
            },
            helps: {
                slug: 'URL publikoan erabiltzen da. Hobe laburra, egonkorra eta hutsunerik gabea izatea.',
                name: 'Aukerakoa. Hutsik uzten baduzu, orri publikoan logotipoa eta gainerako edukia bakarrik erakutsiko dira.',
                description: 'Aukerakoa. Orri honek zer zerbitzu hartzen dituen edo norentzat den azaltzeko balio du.',
                logoUrl: 'Erabili irudi arin eta publikoa, adibidez zerbitzuaren logotipoa.',
                theme: 'Orri publikoaren itxurari bakarrik eragiten dio.',
                public: 'Desaktibatzen baduzu, orria existitzen jarraituko du baina ez da publiko gisa agertuko.',
                noMonitors: 'Monitore gabe gorde dezakezu, baina ez du egoera operatiborik erakutsiko gutxienez bat hautatu arte.',
                noSearchResults: 'Ez dago uneko bilaketarekin bat datorren monitorerik.',
                selectedCount: '{count} hautatuta',
                orderItem: '{index}. {label}',
                urlPreview: 'URLaren aurrebista'
            },
            themes: {
                light: 'Argia',
                dark: 'Iluna'
            },
            actions: {
                selectAll: 'Hautatu guztiak',
                clear: 'Garbitu',
                saveChanges: 'Gorde aldaketak',
                createPage: 'Sortu egoera orria',
                moveUp: 'Mugitu gora',
                moveDown: 'Mugitu behera',
                viewPublic: 'Ikusi orri publikoa',
                viewIncidents: 'Ikusi gorabeherak',
                openPublicPage: 'Ireki orri publikoa'
            },
            listTitle: 'Nire egoera orriak',
            loading: 'Kargatzen...',
            empty: 'Ez dago egoera orririk sortuta',
            detailTitle: 'Xehetasuna: {name}',
            detailSubtitle: 'Orri publikoaren laburpen operatiboa eta ikusgai dagoen konfigurazioa.',
            cards: {
                visibility: 'Ikusgarritasuna',
                effectiveUrl: 'Benetako URLa',
                monitors: 'Monitoreak',
                description: 'Deskribapena',
                unavailable: 'Ez dago erabilgarri',
                internalRoute: 'Barne bide publikoa',
                assigned: '{count} esleituta',
                statusSummary: '{up} martxan, {down} erorita',
                noDescription: 'Ez dago deskribapenik konfiguratuta',
                originName: 'Jatorrizko izena',
                lastCheck: 'Azken egiaztapena',
                noLastCheck: 'Daturik ez',
                noMonitors: 'Ez dago esleitutako monitorerik.'
            },
            deleteTitle: 'Ezabatu egoera orria',
            deletePrompt: '"{name}" egoera orria ezabatu?',
            publicRoute: 'Barne bide publikoa'
        },
        monitorDetail: {
            loading: 'Xehetasunak kargatzen...',
            notFound: 'Monitorea ez da aurkitu',
            status: {
                paused: 'Pausatuta',
                operational: 'Martxan',
                down: 'Erorita',
                laligaBlocked: 'LaLigako blokeoa',
                unknown: 'Ezezaguna'
            },
            buttons: {
                alerts: 'Alertak',
                resetHistory: 'Historia berrezarri',
                resetting: 'Berrezartzen...',
                pauseMonitor: 'Pausatu monitorea',
                resumeMonitor: 'Berrekin monitori',
                updating: 'Eguneratzen...'
            },
            errors: {
                updateStatus: 'Ezin izan da monitoreren egoera eguneratu',
                resetHistory: 'Ezin izan da monitoreren historia berrezarri',
                dismissIncident: 'Ezin izan da gorabehera baztertu',
                saveUptimeOverride: 'Ezin izan da uptime doikuntza eskuzkoa gorde',
                resetUptimeOverride: 'Ezin izan da uptime doikuntza eskuzkoa berrezarri',
                resetUptimeOverrides: 'Ezin izan dira uptime doikuntza eskuzkoak berrezarri',
                loadAlerts: 'Ezin izan da alerten konfigurazioa kargatu',
                saveAlerts: 'Ezin izan da alerten konfigurazioa gorde'
            },
            alerts: {
                title: 'Konfiguratu alertak',
                loading: 'Alerten konfigurazioa kargatzen...',
                languageLabel: 'Komunikazioaren hizkuntza',
                languageHelp: 'Monitor honen email, Telegram mezu eta webhook payloaden bloke lokalizatuan erabiliko da.',
                emailEnabled: 'Bidali alertak posta elektronikoz',
                emailRecipients: 'Posta elektronikoaren hartzaileak',
                addEmail: 'Gehitu emaila',
                removeEmail: 'Ezabatu hartzailea',
                emailHelp: 'Banaka gehi ditzakezu edo hainbat itsatsi komaz, puntu eta komaz edo lerro-jauziz bereizita.',
                telegramEnabled: 'Bidali Telegram alertak',
                telegramChatIds: 'Telegram txatak',
                telegramHelp: 'Gehitu chat ID edo kanal erabiltzaile bat edo gehiago komaz, puntu eta komaz edo lerro-jauziz bereizita. Bot-ak helmugarako sarbidea izan behar du.',
                webhookEnabled: 'Bidali webhook alertak',
                webhookUrl: 'Webhook URLa',
                notificationScopeLabel: 'Jakinarazpen mota',
                notificationScopeHelp: 'Lehenespenez, kanal honek LaLigako blokeotzat hautemandako gorabeherak isilarazten ditu.',
                notificationScopeAll: 'Jakinarazpen guztiak',
                notificationScopeExcludeLaLiga: 'Denak LaLigako gorabeherak izan ezik',
                reminderMinutes: 'Oroigarria (minutuak)',
                reminderHelp: 'Erabili 0 aldizkako oroigarriak desaktibatzeko.',
                recipientsPlaceholder: 'ops@zuredomeinua.com',
                telegramPlaceholder: '-1001234567890, @nire_kanala',
                webhookPlaceholder: 'https://hooks.zuredomeinua.com/uptime',
                save: 'Gorde alertak'
            },
            cards: {
                currentStatus: 'Uneko egoera',
                lastCheck: 'Azken egiaztapena',
                interval: 'Tartea'
            },
            technical: {
                title: 'Datu teknikoak',
                sslTitle: 'SSL ziurtagiria',
                sslDescription: 'Azken TLS ikuskapenean hautemandako informazioa.',
                cdnTitle: 'CDN',
                cdnDescription: 'HTTP erantzunaren goiburuetatik ondorioztatutako hornitzailea.',
                subject: 'Subjektua',
                issuer: 'Jaulkitzailea',
                validUntil: 'Baliozkoa honaino',
                checkedAt: 'Egiaztatua',
                provider: 'Hornitzailea',
                error: 'Errorea',
                notDetected: 'Ez da hauteman',
                pending: 'Zain',
                sslDaysRemaining: '{days} egun barru iraungiko da',
                sslExpiresToday: 'Gaur iraungiko da',
                sslExpired: 'Ziurtagiria iraungita',
                sslError: 'Ziurtagiri errorea'
            },
            descriptions: {
                paused: 'Monitorea pausatuta dago. Azken egiaztapena erreferentzia historiko gisa mantentzen da.',
                up: 'Azken egiaztapen osoa ondo amaitu da.',
                laligaBlocked: 'LaLigarekin lotutako blokeoa hauteman da. Sarbideak huts egin dezake erabiltzaile batzuentzat bakarrik, haien ISP-aren arabera.',
                downAndLaLigaBlocked: 'Monitorea erorita dago eta gainera LaLigarekin lotutako blokeoa ere hauteman da. Bi egoerak batera gerta daitezke.',
                down: 'Azken egiaztapen osoak huts egin du.',
                unknown: 'Oraindik ez dago azken egiaztapenaren emaitza argirik.'
            },
            never: 'Inoiz ez',
            uptimeHistory: 'Erabilgarritasunaren historia (90 egun)',
            ninetyDaysAgo: 'Duela 90 egun',
            today: 'Gaur',
            noData: 'Daturik ez',
            trendTitle: 'Erabilgarritasun joera (%)',
            uptimeEditor: {
                title: 'Doitu erabilgarritasuna {date} egunerako',
                description: 'Eskuz aldatu egun honetako erabilgarritasun ehunekoa. Aldaketak 24h, 7e, 30e eta 90e laburpenak eguneratzen ditu.',
                percentageLabel: 'Erabilgarritasun ehunekoa',
                percentageHelp: 'Sartu 0 eta 100 arteko balioa, gehienez hiru dezimalekin.',
                setPerfect: 'Jarri %100ean',
                reset: 'Berrezarri',
                resetting: 'Berrezartzen...',
                resetAll: 'Berrezarri eskuzko doikuntzak',
                resettingAll: 'Doikuntzak berrezartzen...'
            },
            uptimeTooltip: {
                exactPercentage: 'Ehuneko zehatza',
                realChecks: 'Benetako egiaztapenak',
                averageWeight: 'Batez besteko pisua',
                manualOverride: 'Eskuzko doikuntza',
                manualOverrideYes: 'Bai',
                manualOverrideNo: 'Ez'
            },
            incidentsTitle: 'Gorabeheren historia',
            noIncidents: 'Ez dago erregistratutako gorabeherarik',
            incidentOpen: 'Gorabehera aktiboa',
            incidentResolved: 'Konpondutako gorabehera',
            incidentDismissed: 'Baztertutako gorabehera',
            incidentLaLiga: 'LaLigako blokeoa',
            incidentDuration: 'Iraupena: {seconds}s',
            dismissedFromPublic: 'Ikuspegi publikotik ezkutatuta',
            recentChecks: 'Azken egiaztapenak',
            checkOk: 'OK',
            checkFail: 'Hutsa',
            resetModalTitle: 'Berrezarri monitoreren historia',
            resetModalDescription: 'Honek ezabatuko ditu monitore honen azken egiaztapen ikusgaia, egiaztapen historia, gorabeherak eta sortutako alertak.',
            resetModalState: 'Monitorea {status} egoeran geratuko da hurrengo egiaztapenera arte.',
            confirmReset: 'Berretsi berrezarpena'
        }
    }
};

function normalizeLanguage(value) {
    const shortLanguage = String(value || DEFAULT_LANGUAGE).toLowerCase().slice(0, 2);
    return localeMap[shortLanguage] ? shortLanguage : DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

    const storedLanguage = window.localStorage.getItem(STORAGE_KEY);
    if (storedLanguage) {
        return normalizeLanguage(storedLanguage);
    }

    return normalizeLanguage(window.navigator.language);
}

function getNestedValue(source, path) {
    return path.split('.').reduce((current, segment) => current?.[segment], source);
}

function interpolateMessage(message, values = {}) {
    return String(message).replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
}

const I18nContext = createContext(null);

export function LanguageProvider({ children }) {
    const [language, setLanguageState] = useState(getInitialLanguage);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        window.localStorage.setItem(STORAGE_KEY, language);
        document.documentElement.lang = localeMap[language] || localeMap[DEFAULT_LANGUAGE];
    }, [language]);

    const value = useMemo(() => {
        const messages = translations[language] || translations[DEFAULT_LANGUAGE];

        return {
            language,
            locale: localeMap[language] || localeMap[DEFAULT_LANGUAGE],
            options: languageOptions,
            setLanguage: (nextLanguage) => setLanguageState(normalizeLanguage(nextLanguage)),
            t: (path, values) => {
                const translated = getNestedValue(messages, path)
                    ?? getNestedValue(translations[DEFAULT_LANGUAGE], path)
                    ?? path;

                return typeof translated === 'string'
                    ? interpolateMessage(translated, values)
                    : translated;
            }
        };
    }, [language]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const context = useContext(I18nContext);

    if (!context) {
        throw new Error('useI18n debe usarse dentro de LanguageProvider');
    }

    return context;
}